<template>
    <HomeFrame class="home" header-value="0" hero-image="1-1.png" :hero-gap="0">
        <div class="main">
            <section class="intro-wrap" :style="introWrapStyle">
                <div class="section-shell">
                    <section class="action-section">
                        <div
                            class="action-card"
                            v-for="card in actionCards"
                            :key="card.title"
                            @click="handleAction(card)"
                        >
                            <div class="action-title">{{ card.title }}</div>
                            <div class="action-button">{{ card.button }}</div>
                        </div>
                    </section>

                    <section class="content-section intro-section">
                        <div class="section-title">
                            {{ $t("home.expoProfileTitle") }}
                        </div>
                        <div class="intro-panel">
                            <div class="intro-copy" :class="{ en: isEnglish }">
                                {{ $t("home.expoProfileText") }}
                            </div>
                            <div class="intro-media">
                                <video
                                    class="intro-video"
                                    :src="introVideo"
                                    controls
                                    preload="metadata"
                                ></video>
                            </div>
                        </div>
                    </section>
                </div>
            </section>

            <section class="data-section" :style="dataWrapStyle">
                <div class="section-shell">
                    <div class="section-title section-title-light">
                        {{ dataTitle }}
                    </div>
                    <div class="stats-grid">
                        <div
                            class="stat-card"
                            v-for="item in statsList"
                            :key="item.label"
                        >
                            <div class="stat-value">{{ item.value }}</div>
                            <div class="stat-meta">
                                <div class="stat-label">{{ item.label }}</div>
                                <div class="stat-unit">{{ item.unit }}</div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            <section class="content-section news-section">
                <div class="section-shell">
                    <div class="section-title">
                        {{ workTitle }}
                    </div>
                    <div class="news-panel">
                        <div class="news-list">
                            <div
                                class="news-item"
                                v-for="item in featuredNews"
                                :key="item.id"
                                @click="openWorkNews(item)"
                            >
                                <div class="news-date">{{ formatDate(item.date) }}</div>
                                <div class="news-text">{{ item.title }}</div>
                            </div>
                        </div>
                        <div class="news-cover">
                            <swiper
                                v-if="newsCarouselItems.length > 1"
                                class="news-carousel"
                                :options="newsSwiperOption"
                            >
                                <swiper-slide
                                    v-for="item in newsCarouselItems"
                                    :key="item.id"
                                >
                                    <div
                                        class="news-carousel-slide"
                                        @click="openWorkNews(item)"
                                    >
                                        <img :src="item.cover" :alt="item.title" />
                                        <div class="news-carousel-caption">
                                            {{ item.title }}
                                        </div>
                                    </div>
                                </swiper-slide>
                                <div
                                    slot="pagination"
                                    class="swiper-pagination news-carousel-pagination"
                                ></div>
                            </swiper>
                            <div
                                v-else-if="newsCarouselItems[0]"
                                class="news-carousel-slide news-carousel-slide-single"
                                @click="openWorkNews(newsCarouselItems[0])"
                            >
                                <img
                                    :src="newsCarouselItems[0].cover"
                                    :alt="newsCarouselItems[0].title"
                                />
                                <div class="news-carousel-caption">
                                    {{ newsCarouselItems[0].title }}
                                </div>
                            </div>
                        </div>
                    </div>
                    <div class="news-actions">
                        <button
                            type="button"
                            class="news-more-button"
                            @click="openWorkNewsList"
                        >
                            {{ newsMoreText }}
                        </button>
                    </div>
                </div>
            </section>

            <section class="gallery-section">
                <div class="section-shell">
                    <div class="section-title section-title-light">
                        {{ galleryTitle }}
                    </div>
                    <div class="gallery-marquee">
                        <div
                            class="gallery-item"
                            v-for="(photo, index) in duplicatedGalleryPhotos"
                            :key="photo.img + '-' + index"
                        >
                            <img :src="photo.img" :alt="photo.alt" />
                        </div>
                    </div>
                </div>
            </section>

            <section class="directory-section">
                <div class="section-shell">
                    <div class="section-title">{{ directoryTitle }}</div>
                    <div class="section-note">{{ directoryNote }}</div>
                    <div class="logo-grid">
                        <div
                            class="logo-card"
                            v-for="item in visibleLogoList"
                            :key="item.img || item.name"
                        >
                            <img :src="item.img" :alt="item.name || 'logo'" />
                        </div>
                    </div>
                    <div
                        v-if="shopLogoList.length > logoPreviewCount"
                        class="directory-more"
                        @click="openDirectoryMore"
                    >
                        <div class="directory-more-text">{{ moreText }}</div>
                        <div class="directory-more-icon"></div>
                    </div>
                </div>
            </section>
        </div>
    </HomeFrame>
</template>

<script>
import HomeFrame from "@/components/HomeFrame.vue";
import { ABOUT_SECTIONS, ABOUT_WORK_NEWS } from "@/data/aboutShow";
import { firmsList } from "@/utils/map";
import { gm } from "@/utils/gm";

export default {
    components: {
        HomeFrame,
    },
    data() {
        return {
            workNewsList: [],
            shopLogoList: [],
            logoPreviewCount: 40,
            introVideo: require("@/assets/file/video.mp4"),
        };
    },
    created() {
        this.shopLogoList = firmsList.filter((item) => item.img);
        this.workNewsList = [...ABOUT_WORK_NEWS];
    },
    mounted() {
        this.toggleHomeResponsiveLayout(true);
    },
    beforeDestroy() {
        this.toggleHomeResponsiveLayout(false);
    },
    computed: {
        isEnglish() {
            return this.$i18n.locale === "en";
        },
        actionCards() {
            return [
                {
                    title: this.isEnglish ? "Exhibitor Registration" : "展商报名",
                    button: this.isEnglish ? "Click to register" : "点击报名",
                    type: "register",
                    value: 1,
                },
                {
                    title: this.isEnglish ? "Visitor Registration" : "观众登记",
                    button: this.isEnglish ? "Click to register" : "点击登记",
                    type: "register",
                    value: 2,
                },
                {
                    title: this.isEnglish ? "Concurrent Events" : "本届亮点",
                    button: this.isEnglish ? "View more" : "查看更多",
                    type: "route",
                    value: "/highlights",
                },
                {
                    title: this.isEnglish ? "Download Center" : "下载中心",
                    button: this.isEnglish ? "Click to open" : "点击进入",
                    type: "route",
                    value: "/downloads",
                },
                {
                    title: this.isEnglish ? "Live Room" : "直播间",
                    button: this.isEnglish ? "Click to watch" : "点击观看",
                    type: "live",
                },
            ];
        },
        statsList() {
            return this.isEnglish
                ? [
                      { value: "2", label: "Halls", unit: "" },
                      { value: "1200", label: "Standard Booths", unit: "" },
                      { value: "2.4", label: "Planned Scale", unit: "10k sq.m." },
                      { value: "4", label: "Expected Visitors", unit: "10k people" },
                  ]
                : [
                      { value: "2", label: "展馆", unit: "个" },
                      { value: "1200", label: "标准展位", unit: "个" },
                      { value: "2.4", label: "预计展出规模", unit: "万平方米" },
                      { value: "4", label: "预计观众", unit: "万人" },
                  ];
        },
        dataTitle() {
            return this.isEnglish ? "EXPO DATA" : "本届博览会大数据";
        },
        workTitle() {
            return this.isEnglish ? "WORK UPDATES" : "工作动态";
        },
        galleryTitle() {
            return this.isEnglish ? "PREVIOUS PHOTOS" : "往届照片";
        },
        directoryTitle() {
            return this.isEnglish ? "2025 EXHIBITOR DIRECTORY" : "2025展商名录";
        },
        directoryNote() {
            return this.isEnglish
                ? "Exhibitors are displayed in random order"
                : "展商顺序随机排列";
        },
        moreText() {
            return this.isEnglish ? "View More" : "查看更多";
        },
        newsMoreText() {
            return this.isEnglish ? "Show More" : "显示更多";
        },
        featuredNews() {
            return this.workNewsList.slice(0, 5);
        },
        newsCarouselItems() {
            return this.workNewsList.filter((item) => item.cover);
        },
        newsSwiperOption() {
            return {
                loop: this.newsCarouselItems.length > 1,
                observer: true,
                observeParents: true,
                watchOverflow: true,
                speed: 600,
                autoplay:
                    this.newsCarouselItems.length > 1
                        ? {
                              delay: 4500,
                              disableOnInteraction: false,
                          }
                        : false,
                pagination: {
                    el: ".news-carousel-pagination",
                    clickable: true,
                },
                a11y: false,
            };
        },
        galleryPhotos() {
            return [
                {
                    img: gm("微信图片_20260307141220_432_14.jpg"),
                    alt: this.isEnglish ? "Expo photo 1" : "往届照片1",
                },
                {
                    img: gm("微信图片_20260307141221_433_14.jpg"),
                    alt: this.isEnglish ? "Expo photo 2" : "往届照片2",
                },
                {
                    img: gm("微信图片_20260307141222_434_14.jpg"),
                    alt: this.isEnglish ? "Expo photo 3" : "往届照片3",
                },
                {
                    img: gm("微信图片_20260307141224_435_14.png"),
                    alt: this.isEnglish ? "Expo photo 4" : "往届照片4",
                },
                {
                    img: gm("微信图片_20260307141225_436_14.jpg"),
                    alt: this.isEnglish ? "Expo photo 5" : "往届照片5",
                },
                {
                    img: gm("微信图片_20260307141226_437_14.jpg"),
                    alt: this.isEnglish ? "Expo photo 6" : "往届照片6",
                },
                {
                    img: gm("微信图片_20260307141227_438_14.png"),
                    alt: this.isEnglish ? "Expo photo 7" : "往届照片7",
                },
                {
                    img: gm("微信图片_20260307141228_439_14.jpg"),
                    alt: this.isEnglish ? "Expo photo 8" : "往届照片8",
                },
                {
                    img: gm("微信图片_20260307143109_441_14.jpg"),
                    alt: this.isEnglish ? "Expo photo 9" : "往届照片9",
                },
                {
                    img: gm("微信图片_20260307143110_442_14.jpg"),
                    alt: this.isEnglish ? "Expo photo 10" : "往届照片10",
                },
                {
                    img: gm("微信图片_20260307143112_443_14.jpg"),
                    alt: this.isEnglish ? "Expo photo 11" : "往届照片11",
                },
                {
                    img: gm("微信图片_20260307143114_444_14.jpg"),
                    alt: this.isEnglish ? "Expo photo 12" : "往届照片12",
                },
                {
                    img: gm("微信图片_20260307143115_445_14.jpg"),
                    alt: this.isEnglish ? "Expo photo 13" : "往届照片13",
                },
                {
                    img: gm("微信图片_20260307143116_446_14.jpg"),
                    alt: this.isEnglish ? "Expo photo 14" : "往届照片14",
                },
                {
                    img: gm("微信图片_20260307143118_447_14.jpg"),
                    alt: this.isEnglish ? "Expo photo 15" : "往届照片15",
                },
            ];
        },
        duplicatedGalleryPhotos() {
            return [...this.galleryPhotos, ...this.galleryPhotos];
        },
        visibleLogoList() {
            return this.shopLogoList.slice(0, this.logoPreviewCount);
        },
        introWrapStyle() {
            return {
                backgroundImage: `url(${require("@/assets/image/home-bg-1.png")})`,
            };
        },
        dataWrapStyle() {
            return {
                backgroundImage: `url(${require("@/assets/image/home-bg-2.png")})`,
                backgroundRepeat: "no-repeat",
                backgroundPosition: "center center",
                backgroundSize: "cover",
            };
        },
    },
    methods: {
        toggleHomeResponsiveLayout(enabled) {
            const appRoot = document.getElementById("app");

            if (appRoot) {
                appRoot.classList[enabled ? "add" : "remove"](
                    "home-responsive-layout"
                );
            }
        },
        register(type) {
            if (type === 1) {
                this.goRoute("/contactUs");
                return;
            }
            if (type === 2) {
                window.open("https://z.zcyit.cn/green/#/?regid=1191", "_blank");
                return;
            }
            this.$router.push("/register?type=" + type);
        },
        goRoute(path) {
            if (path !== this.$route.path) {
                this.$router.push(path);
                window.scrollTo(0, 0);
            }
        },
        openLink(link) {
            if (link) {
                window.open(link, "_blank");
            }
        },
        openWorkNews(item) {
            if (!item) {
                return;
            }

            this.$router.push({
                path: "/about",
                query: {
                    section: ABOUT_SECTIONS.news,
                    article: item.id,
                },
            });
            window.scrollTo(0, 0);
        },
        showComingSoonDialog() {
            this.$alert(
                this.$t("exhibitor.comingSoon"),
                this.$t("exhibitor.tipTitle"),
                {
                    confirmButtonText: this.$t("exhibitor.confirm"),
                    showClose: false,
                }
            );
        },
        formatDate(value) {
            return value ? value.replace(/-/g, "/") : "";
        },
        handleAction(card) {
            if (card.type === "register") {
                this.register(card.value);
                return;
            }
            if (card.type === "route") {
                this.goRoute(card.value);
                return;
            }
            if (card.type === "live") {
                this.showComingSoonDialog();
            }
        },
        openGalleryMore() {
            this.goRoute("/about");
        },
        openWorkNewsList() {
            this.$router.push({
                path: "/about",
                query: {
                    section: ABOUT_SECTIONS.news,
                },
            });
            window.scrollTo(0, 0);
        },
        openDirectoryMore() {
            this.$router.push({
                path: "/exhibitor",
                query: {
                    index: "1",
                },
            });
            window.scrollTo(0, 0);
        },
    },
};
</script>

<style scoped lang="less">
.home {
    width: 100%;
    min-width: 0;
}

.main {
    background:
        linear-gradient(120deg, rgba(219, 225, 235, 0.32) 0, rgba(219, 225, 235, 0.32) 1px, transparent 1px, transparent 60px),
        linear-gradient(145deg, #eff2f6 0%, #ffffff 42%, #f3f5f8 100%);
    background-size: 92px 92px, auto;
}

.section-shell {
    width: 88%;
    margin: 0 auto;
}

.section-title {
    font-size: 36px;
    line-height: 1.2;
    font-weight: 700;
    color: #333333;
    text-align: center;
}

.section-title-light {
    color: #ffffff;
}

.intro-wrap {
    background-repeat: no-repeat;
    background-position: center top;
    background-size: cover;
    padding: 50px 0 54px;
}

.action-section {
    display: grid;
    grid-template-columns: repeat(5, minmax(0, 1fr));
    gap: 16px;
}

.action-card {
    min-height: 110px;
    height: 140px; padding: 20px 0;box-sizing:border-box;
    border-radius: 10px;
    background: linear-gradient(180deg, #5a3198 0%, #2843a5 100%);
    color: #ffffff;
    text-align: center;
    cursor: pointer;
    box-shadow: 0 14px 28px rgba(44, 56, 139, 0.18);
    display:flex; flex-direction:row; flex-wrap:wrap; justify-content:center; overflow:hidden; clear:both;align-items:center;
}

.action-title {box-sizing:border-box;
    width: 100%;
    display: block;
    font-size: 30px;
    line-height: 1.2;
    font-weight: 500;
    margin: 0 ;padding: 0;
}

.action-button {
    width: fit-content;box-sizing:border-box;
    min-width: 120px;
    max-width: 100%;
    margin: 0px auto 0;
    padding: 5px 16px;
    border: 1px solid rgba(255, 255, 255, 0.82);
    font-size: 16px;
    line-height: 1;
    border-radius: 5px;
}

.content-section {
    padding: 72px 0 0;
}

.intro-panel,
.news-panel {
    margin-top: 34px;
    display: grid;
    grid-template-columns: minmax(0, 1.18fr) minmax(520px, 0.82fr);
    gap: 54px;
    align-items: center;
}

.intro-copy {
    min-height: 252px;
    padding: 42px 48px;
    font-size: 24px;
    line-height: 50px;
    color: #333333;
    text-align: justify;
    text-indent: 2em;
}

.intro-copy.en {
    text-indent: 0;
}

.section-title,
.action-title,
.stat-label,
.stat-unit,
.news-text,
.section-note,
.directory-more-text,
.action-button {
    overflow-wrap: anywhere;
}

.intro-media,
.news-cover,
.gallery-item {
    position: relative;
    overflow: hidden;
}

.intro-media,
.news-cover {
    cursor: pointer;
}

.intro-video,
.news-cover img,
.gallery-item img,
.logo-card img {
    width: 100%;
    display: block;
}

.intro-media {
    border: 1px solid rgba(29, 50, 116, 0.08);
}

.intro-video {
    min-height: 252px;
    object-fit: cover;
}

.data-section,
.gallery-section {
    background-repeat: no-repeat;
    background-position: center center;
    background-size: cover;
}

.data-section {
    padding: 32px 0 36px;
}

.stats-grid {
    display: grid;
    grid-template-columns: repeat(4, minmax(0, 1fr));
    margin-top: 30px;
}

.stat-card {
    min-height: 164px;
    padding: 28px 26px 24px;
    align-items: baseline;
    gap: 14px;
    background: rgba(31, 53, 145, 0.2);
    display:flex; flex-direction:row; flex-wrap:no-wrap; justify-content:center; overflow:hidden; clear:both;align-items:center;
}

.stat-value {
    font-size: 80px;
    line-height: 1;
    font-weight: 700;
    font-style: italic;
    color: #2ce1ff;
}

.stat-meta {
    color: #ffffff;
}

.stat-label {
    font-size: 26px;
    line-height: 1.35;
    font-weight: 600;
}

.stat-unit {
    margin-top: 8px;
    font-size: 20px;
}

.news-section {
    padding: 58px 0 70px;
}

.news-list {
    display: flex;
    flex-direction: column;
    gap: 22px;
}

.news-item {
    display: grid;
    grid-template-columns: 180px minmax(0, 1fr);
    cursor: pointer;
    color: #333333;
}

.news-item:hover .news-date,
.news-item:hover .news-text {
    color: #184ef6;
}

.news-date {
    color: #333;
    font-size: 24px;
    line-height: 1.65;
}

.news-text {
    font-size: 24px;
    line-height: 1.65;
}

.news-cover {
    height: 380px;
    border-radius: 18px;
    background: #e9edf5;
    box-shadow: 0 20px 40px rgba(23, 47, 108, 0.12);
}

.news-actions {
    margin-top: 28px;
    display: flex;
    justify-content: center;
}

.news-more-button {
    min-width: 168px;
    padding: 14px 28px;
    border: 1px solid #184ef6;
    border-radius: 999px;
    background: #ffffff;
    color: #184ef6;
    font-size: 18px;
    line-height: 1.2;
    font-weight: 600;
    cursor: pointer;
    transition:
        background-color 0.2s ease,
        color 0.2s ease,
        box-shadow 0.2s ease,
        transform 0.2s ease;
}

.news-more-button:hover {
    background: #184ef6;
    color: #ffffff;
    box-shadow: 0 12px 24px rgba(24, 78, 246, 0.22);
    transform: translateY(-1px);
}

.news-carousel,
.news-carousel-slide,
.news-carousel-slide-single {
    width: 100%;
    height: 100%;
    position: relative;
}

.news-carousel-slide {
    cursor: pointer;
}

.news-carousel-slide img {
    height: 100%;
    object-fit: cover;
    transition: transform 0.35s ease;
}

.news-carousel-slide:hover img {
    transform: scale(1.04);
}

.news-carousel-caption {
    position: absolute;
    right: 0;
    bottom: 0;
    left: 0;
    padding: 22px 26px;
    background: linear-gradient(
        180deg,
        rgba(7, 20, 52, 0) 0%,
        rgba(7, 20, 52, 0.82) 100%
    );
    color: #ffffff;
    font-size: 20px;
    line-height: 1.5;
}

.news-carousel-pagination {
    bottom: 16px !important;
}

::v-deep .news-carousel-pagination .swiper-pagination-bullet {
    width: 10px;
    height: 10px;
    background: rgba(255, 255, 255, 0.56);
    opacity: 1;
}

::v-deep .news-carousel-pagination .swiper-pagination-bullet-active {
    background: #ffffff;
}

.gallery-section {
    padding: 50px 0 56px;
    background-image:
        url("~@/assets/image/home-bg-3.png");
}

.gallery-marquee {
    margin-top: 45px;
    margin-bottom: 42px;
    display: flex;
    gap: 18px;
    width: max-content;
    animation: gallery-scroll 48s linear infinite;
}

.gallery-section .section-shell {
    overflow: hidden;
}

.gallery-item {
    width: 320px;
    flex: 0 0 auto;
}

.gallery-item img {
    height: 225px;
    object-fit: cover;
}

.gallery-marquee:hover {
    animation-play-state: paused;
}

@keyframes gallery-scroll {
    from {
        transform: translateX(0);
    }
    to {
        transform: translateX(calc(-50% - 9px));
    }
}

.directory-section {
    padding: 62px 0 42px;
    background: #fff;
}

.section-note {
    margin-top: 12px;
    text-align: center;
    color: #666666;
    font-size: 20px;
}

.logo-grid {
    margin-top: 38px;
    display: grid;
    grid-template-columns: repeat(8, minmax(0, 1fr));
    gap: 24px 28px;
}

.logo-card {
    min-height: 72px;
    display: flex;
    align-items: center;
    justify-content: center;
}

.logo-card img {
    max-width: 100%;
    max-height: 84px;
    object-fit: contain;
}

.directory-more {
    margin: 26px auto 0;
    width: fit-content;
    color: #8f8f8f;
    font-size: 18px;
    text-align: center;
    cursor: pointer;
}

.directory-more-icon {
    position: relative;
    width: 20px;
    height: 18px;
    margin: 10px auto 0;
}

.directory-more-icon::before,
.directory-more-icon::after {
    content: "";
    position: absolute;
    left: 50%;
    width: 12px;
    height: 12px;
    border-right: 1px solid #b0b0b0;
    border-bottom: 1px solid #b0b0b0;
    transform: translateX(-50%) rotate(45deg);
}

.directory-more-icon::before {
    top: -2px;
}

.directory-more-icon::after {
    top: 4px;
}


@media (max-width: 1366px) {
    .section-shell {
        width: 90%;
    }

    .section-title {
        font-size: 32px;
    }

    .intro-wrap {
        padding: 42px 0 48px;
    }

    .content-section {
        padding: 60px 0 0;
    }

    .action-section {
        grid-template-columns: repeat(3, minmax(0, 1fr));
        gap: 14px;
    }

    .action-card {
        height: auto;
        min-height: 132px;
        padding: 18px 14px;
    }

    .action-title {
        font-size: 24px;
    }

    .action-button {
        min-width: 0;
        font-size: 15px;
    }

    .intro-panel,
    .news-panel {
        gap: 36px;
    }

    .intro-copy {
        padding: 34px 36px;
        font-size: 21px;
        line-height: 1.9;
    }

    .stat-card {
        min-height: 150px;
        padding: 24px 20px;
    }

    .stat-value {
        font-size: 62px;
    }

    .stat-label {
        font-size: 22px;
    }

    .stat-unit {
        font-size: 18px;
    }

    .news-date,
    .news-text {
        font-size: 21px;
        line-height: 1.55;
    }

    .news-cover {
        height: 340px;
    }

    .news-more-button {
        font-size: 17px;
    }

    .news-carousel-caption {
        padding: 18px 22px;
        font-size: 18px;
    }

    .gallery-marquee {
        gap: 16px;
    }

    .gallery-item img {
        height: 210px;
    }

    .section-note {
        font-size: 18px;
    }

    .logo-grid {
        gap: 20px 22px;
    }

}

@media (max-width: 1200px) {
    .section-shell {
        width: 92%;
    }

    .section-title {
        font-size: 30px;
    }

    .intro-wrap {
        padding: 38px 0 44px;
    }

    .content-section,
    .news-section {
        padding-top: 52px;
    }

    .action-section {
        grid-template-columns: repeat(3, minmax(0, 1fr));
    }

    .intro-panel,
    .news-panel {
        grid-template-columns: 1fr;
        gap: 24px;
    }

    .intro-copy {
        min-height: 0;
        padding: 28px 30px;
        font-size: 19px;
        line-height: 1.8;
    }

    .intro-video {
        min-height: 0;
    }

    .stats-grid {
        grid-template-columns: repeat(2, minmax(0, 1fr));
    }

    .stat-card {
        border-bottom: 1px solid rgba(255, 255, 255, 0.18);
        min-height: 140px;
    }

    .stat-card:nth-last-child(-n + 2) {
        border-bottom: none;
    }

    .news-item {
        grid-template-columns: 150px minmax(0, 1fr);
    }

    .news-date,
    .news-text {
        font-size: 19px;
        line-height: 1.5;
    }

    .news-cover {
        height: 320px;
    }

    .logo-grid {
        grid-template-columns: repeat(5, minmax(0, 1fr));
    }

}

@media (max-width: 992px) {
    .section-title {
        font-size: 28px;
    }

    .content-section,
    .news-section {
        padding-top: 46px;
    }

    .action-section {
        grid-template-columns: repeat(2, minmax(0, 1fr));
    }

    .action-card {
        min-height: 124px;
    }

    .action-title {
        font-size: 22px;
    }

    .intro-copy {
        padding: 24px 24px;
        font-size: 18px;
        line-height: 1.75;
    }

    .stat-card {
        gap: 10px;
    }

    .stat-value {
        font-size: 54px;
    }

    .stat-label {
        font-size: 20px;
    }

    .stat-unit {
        font-size: 17px;
    }

    .news-item {
        grid-template-columns: 1fr;
        gap: 4px;
    }

    .news-cover {
        height: 300px;
    }

    .logo-grid {
        grid-template-columns: repeat(4, minmax(0, 1fr));
    }

}

@media (max-width: 768px) {
    .section-shell {
        width: 94%;
    }

    .section-title {
        font-size: 24px;
    }

    .intro-wrap {
        padding: 32px 0 36px;
    }

    .content-section,
    .news-section,
    .gallery-section,
    .directory-section {
        padding-top: 36px;
    }

    .action-section,
    .stats-grid {
        grid-template-columns: 1fr;
    }

    .action-card {
        min-height: 0;
        padding: 16px 14px;
    }

    .action-title {
        font-size: 20px;
    }

    .action-button {
        width: 100%;
        padding-left: 12px;
        padding-right: 12px;
    }

    .intro-copy {
        padding: 22px 20px;
        font-size: 17px;
        line-height: 1.75;
    }

    .stat-card {
        min-height: 0;
        padding: 22px 18px;
        gap: 12px;
        border-bottom: 1px solid rgba(255, 255, 255, 0.18);
    }

    .stat-card:nth-last-child(-n + 2) {
        border-bottom: 1px solid rgba(255, 255, 255, 0.18);
    }

    .stat-card:last-child {
        border-bottom: none;
    }

    .stat-value {
        font-size: 46px;
    }

    .news-item {
        grid-template-columns: 1fr;
        gap: 6px;
    }

    .news-date,
    .news-text {
        font-size: 18px;
    }

    .news-cover {
        height: 260px;
    }

    .news-actions {
        margin-top: 24px;
    }

    .news-more-button {
        min-width: 152px;
        padding: 12px 24px;
        font-size: 16px;
    }

    .news-carousel-caption {
        padding: 14px 16px;
        font-size: 16px;
    }

    .gallery-item img {
        height: 190px;
    }

    .logo-grid {
        grid-template-columns: repeat(3, minmax(0, 1fr));
        gap: 18px 16px;
    }

}

@media (max-width: 576px) {
    .section-title {
        font-size: 22px;
    }

    .intro-copy {
        padding: 20px 18px;
        font-size: 16px;
    }

    .news-date,
    .news-text {
        font-size: 16px;
    }

    .news-cover {
        height: 220px;
    }

    .news-more-button {
        width: 100%;
        max-width: 220px;
        padding: 12px 20px;
        font-size: 15px;
    }

    .news-carousel-caption {
        padding: 12px 14px;
        font-size: 14px;
    }

    .gallery-item img {
        height: 170px;
    }

    .section-note,
    .directory-more {
        font-size: 16px;
    }

    .logo-grid {
        grid-template-columns: repeat(4, minmax(0, 1fr));
    }

}
</style>

<style lang="less">
#app.home-responsive-layout {
    min-width: 0;
    width: 100%;
}
</style>
